import { AttendeePriority } from '../interfaces/interface';

export const MAX_ATTACHMENTS = 5;

/** Color map for priority badges — shared across all attendee-related components */
export const PRIORITY_COLORS: Record<AttendeePriority, { background: string; color: string }> = {
  [AttendeePriority.Critical]: { background: '#d91515', color: '#fff' },
  [AttendeePriority.Required]: { background: '#0972d3', color: '#fff' },
  [AttendeePriority.Optional]: { background: '#e9ebed', color: 'rgb(95, 107, 122)' }
};

/**
 * Prompt for AI-powered time slot summary generation.
 */
export const TIMESLOT_SUMMARY_PROMPT = `You are a meeting scheduling assistant. You receive structured JSON data about a meeting and produce a concise scheduling recommendation.

Input
You will receive:

Meeting metadata (title, date, duration, timezone, organizer, on behalf of)
Proposed time slots with vote counts per attendee (thumbs up, thumbs down, or no vote)
Attendee list with: name, level, role (critical/required/optional), EA name (if applicable), proxyFor (name of the principal they are proxying for, if applicable)
Comments on time slots (if any)

Rules
A critical attendee's 👎 eliminates that slot. Flag it with 🚫.
All critical attendees must be covered for a slot to be recommended. If any critical attendee is ⏳, the slot cannot be fully confirmed.
Vote weighting for recommendation: Critical = must-have, Required = strong signal, Optional = nice-to-have.
Comments override votes. If someone voted 👍 but commented "tight, prefer the other", note the conflict.
If an attendee has an EA, the EA's vote counts as the attendee's vote.
If an attendee has a proxy, the proxy's vote counts as the principal's vote. Show the proxy relationship in the attendee breakdown (e.g., "[Proxy Name] (Proxy for [Principal Name])").
No vote (⏳) is a risk, not neutral. Always flag who hasn't voted.

Output Format
You MUST produce valid markdown. Follow this structure exactly. Preserve all line breaks between sections. Use markdown horizontal rules (---) to separate major sections. Do NOT use markdown table syntax (pipes). Use bold labels and lists instead.

# Meeting: [title]

- **Date:** [date or "Multiple dates proposed" if more than one]
- **Duration:** [duration]
- **Timezone:** [timezone]
- **Attendees:** [count] ([critical count] critical, [required count] required, [optional count] optional)
- **Organizer:** [name] (on behalf of [name], if applicable)

---

## Slot Summary

- **[date, start–end] [timezone]** — 👍 [count] 👎 [count] ⏳ [count]
- **[date, start–end] [timezone]** — 👍 [count] 👎 [count] ⏳ [count]

---

## Attendee Breakdown

### 🟥 Critical

- **[Name]** — [Level] — [👍/👎/⏳] [details — if 👎, include "Blocked [slot time]"]

### 🟦 Required

- **[Name]** — [Level] — [👍/👎/⏳] [details — per-slot breakdown if mixed votes]

### ⬜ Optional

- **[Name]** — [Level] — EA: [EA Name or "—"] — [👍/👎/⏳] [details]

---

## Recommendation

[emoji] [recommendation line]

---

## Suggested Actions

- [action]
- [action]
- [action]

IMPORTANT: Do NOT use markdown table syntax (| pipes). Always use bullet lists with bold labels as shown above. Always include markdown horizontal rules (---) between major sections. Always include the timezone in each time slot line. Use bullet points (not numbered lists) for suggested actions. Use "No response" for ⏳ status text. For mixed votes across slots, show per-slot breakdown (e.g., "👍 Slot 1, 👎 Slot 2").

Recommendation logic
If clear winner: ✅ Recommended: [slot]. [One sentence why.]
If slot blocked by critical: 🚫 [slot] is blocked. [Name] (Critical) voted 👎.
If no clear winner: ⚠️ [Explain tradeoff in one sentence.]
If no votes: 📭 No votes received yet.
If tie: 🔄 Tie between [slots]. [Tiebreaker suggestion.]

Rules for suggested actions
Always route outreach through EAs when available
If a critical attendee voted 👎, suggest checking their availability for alternative times via their EA
If only one slot exists, suggest adding alternatives
Keep to 3-4 actions max

Tone
Direct, scannable. No filler. Bullet points. Optimize for a quick glance that tells the organizer exactly what to do next.`;

/**
 * Configuration constants for EA Huddle meeting operations.
 */
export const MEETING_CONSTANTS = {
  ATTENDEE_BATCH_SIZE: 5,
  BATCH_DELAY_MS: 1000,
  PAGE_SIZE: 5,
  FAVORITES_PAGE_SIZE: 10,
  MAX_RECENT_MEETINGS: 100,
  EMPLOYEE_CACHE_TTL_SECONDS: 3600,
  /** Browser-side employee cache TTL (30 minutes). Covers in-memory Map + sessionStorage. */
  BROWSER_CACHE_TTL_MS: 30 * 60 * 1000,
  MAX_SUBJECT_LENGTH: 100,
  MAX_AGENDA_LENGTH: 2000
} as const;

/**
 * Leader EA Email Constants
 *
 * TODO: Migrate this data to a database solution for better maintainability and scalability.
 * Currently as of Oct 2025, there is no service that provides this information, these are stored in wiki pages.
 * https://w.amazon.com/bin/view/Business%26CorporateDevelopment/Admin/SVPMeetings/
 * This approach is acceptable short-term as leader EA assignments change infrequently.
 */

export interface LeaderEAEmail {
  leader: string;
  alias: string;
  eaEmailAddress: string | null;
}

// https://email-list.corp.amazon.com/email-list/email-list.mhtml?action=search&name=steam-only
export const LEADER_EA_EMAIL_DATA: LeaderEAEmail[] = [
  {
    leader: 'Andy Jassy',
    alias: 'ajassy',
    eaEmailAddress: 'aj-ea@amazon.com'
  },
  {
    leader: 'Amit Agarwal',
    alias: 'amit',
    eaEmailAddress: 'amit-ea@amazon.com'
  },
  {
    leader: 'Beth Galetti',
    alias: 'galettib',
    eaEmailAddress: 'galetti-office@amazon.com'
  },
  {
    leader: 'Bob Carrigan',
    alias: 'carrigan',
    eaEmailAddress: null
  },
  {
    leader: 'Brian Olsavsky',
    alias: 'olsavsky',
    eaEmailAddress: 'cfo-assist@amazon.com'
  },
  {
    leader: 'Christine Beauchamp',
    alias: 'cmbeau',
    eaEmailAddress: 'beauchamp-ea@amazon.com'
  },
  {
    leader: 'Colleen Aubrey',
    alias: 'caubrey',
    eaEmailAddress: 'caubrey-ea@amazon.com'
  },
  {
    leader: 'Dave Treadwell',
    alias: 'tread',
    eaEmailAddress: 'tread-ea@amazon.com'
  },
  {
    leader: 'David Zapolsky',
    alias: 'davidz',
    eaEmailAddress: null
  },
  {
    leader: 'Doug Herrington',
    alias: 'dherring',
    eaEmailAddress: 'doug-ea@amazon.com'
  },
  {
    leader: 'Drew Herdener',
    alias: 'herdener',
    eaEmailAddress: 'herdener-ea@amazon.com'
  },
  {
    leader: 'Hudson Thrift',
    alias: 'thrift',
    eaEmailAddress: 'hudson-support@amazon.com'
  },
  {
    leader: 'James Hamilton',
    alias: 'james',
    eaEmailAddress: 'budebbie@amazon.com'
  },
  {
    leader: 'John Felton',
    alias: 'johnfelt',
    eaEmailAddress: 'felton-ea@amazon.com'
  },
  {
    leader: 'Matt Garman',
    alias: 'garman',
    eaEmailAddress: 'garman-executive-office@amazon.com'
  },
  {
    leader: 'Mike Hopkins',
    alias: 'mikehopk',
    eaEmailAddress: null
  },
  {
    leader: 'Neil Lindsay',
    alias: 'nlindsay',
    eaEmailAddress: 'lindsay-ea@amazon.com'
  },
  {
    leader: 'Panos Panay',
    alias: 'panosp',
    eaEmailAddress: 'panos-ea@amazon.com'
  },
  {
    leader: 'Paul Kotas',
    alias: 'kotas',
    eaEmailAddress: 'georgire@amazon.com'
  },
  {
    leader: 'Peter DeSantis',
    alias: 'desantis',
    eaEmailAddress: 'tnaes-team@amazon.com'
  },
  {
    leader: 'Peter Krawiec',
    alias: 'krawiec',
    eaEmailAddress: 'krawiec-ea-team@amazon.com'
  },
  {
    leader: 'Russell Grandinetti',
    alias: 'russ',
    eaEmailAddress: 'russ-ea@amazon.com'
  },
  {
    leader: 'Steve Boom',
    alias: 'sboom',
    eaEmailAddress: 'SB-EA@amazon.com'
  },
  {
    leader: 'Steve Schmidt',
    alias: 'steve',
    eaEmailAddress: 'cso-support@amazon.com'
  },
  {
    leader: 'Udit Madan',
    alias: 'uditm',
    eaEmailAddress: 'udit-ea@amazon.com'
  },
  {
    leader: 'Tobias Straub',
    alias: 'tobias',
    eaEmailAddress: 'tobias-ea@amazon.com'
  },
  {
    leader: 'David Brown',
    alias: 'dbrown',
    eaEmailAddress: 'dbrown-support@amazon.com'
  },
  {
    leader: 'Deepak Singh',
    alias: 'deesingh',
    eaEmailAddress: 'deesingh-support-team@amazon.com'
  },
  {
    leader: 'Dharmesh Mehta',
    alias: 'dharmesm',
    eaEmailAddress: 'dharmesh-ea@amazon.com'
  },
  {
    leader: 'Prasad Kalyanaraman',
    alias: 'kprasad',
    eaEmailAddress: 'tamabav-team@amazon.com'
  },
  {
    leader: 'Swami Sivasubramanian',
    alias: 'swami',
    eaEmailAddress: 'swami-support@amazon.com'
  },
  {
    leader: 'Tim Halladay',
    alias: 'timhall',
    eaEmailAddress: 'halladay-ea@amazon.com'
  },
  {
    leader: 'Vasi Philomin',
    alias: 'vasi',
    eaEmailAddress: 'vasi-support@amazon.com'
  },
  {
    leader: 'Aicha Evans',
    alias: 'aichae',
    eaEmailAddress: null
  },
  {
    leader: 'Candi Castleberry',
    alias: 'candigbl',
    eaEmailAddress: null
  },
  {
    leader: 'Kara Hurst',
    alias: 'karahurs',
    eaEmailAddress: 'khh-assists@amazon.com'
  }
];
